import React, { useEffect, useState } from 'react';
import api, { brainHealthAPI, liveReadinessAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, CheckCircle2, AlertTriangle, Radio, RefreshCw, Zap } from 'lucide-react';

// Turns the "Switch to LIVE" CTA into a real action: shows the readiness checks
// then flips execution_mode to LIVE via the existing backend endpoint.
//
// Iter 222: the readiness fetch previously got stuck on "Checking readiness…"
// when the Radix portal remounted and aborted the in-flight XHR. Fixed by
// (a) using a shared cached promise (liveReadinessAPI) that survives remounts,
// (b) an explicit timeout that surfaces an error state instead of an eternal
// spinner, and (c) a Retry button so users can recover without reopening.
// Cold backend starts can take >12s on the very first call of a session; a 25s
// ceiling avoids a false "error" on cold starts while still rescuing the user
// from a genuinely hung request (Retry warms the shared promise cache).
const READINESS_TIMEOUT_MS = 25000;

export const GoLiveDialog = ({ open, onOpenChange, onSuccess }) => {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autopilot, setAutopilot] = useState(true);

  const loadReadiness = (force = false) => {
    setLoading(true);
    setError(false);
    if (force) liveReadinessAPI.invalidate();

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      liveReadinessAPI.invalidate(); // drop the stuck promise so Retry refetches
      setError(true);
      setLoading(false);
    }, READINESS_TIMEOUT_MS);

    liveReadinessAPI.getCached()
      .then((d) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setReadiness(d);
        setError(false);
        setLoading(false);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        liveReadinessAPI.invalidate();
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!open) return;
    // force=false → reuse the shared in-flight/cached promise across remounts.
    loadReadiness(false);
  }, [open]);

  const goLive = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/tenant/ai/execution-mode', { mode: 'LIVE', enable_autopilot: autopilot });
      brainHealthAPI.invalidate();
      liveReadinessAPI.invalidate();
      toast.success(autopilot
        ? 'Brain is LIVE with full autopilot — the AI will send outreach autonomously.'
        : 'Brain is now LIVE — real outreach will start on your active cadences.');
      onOpenChange(false);
      if (onSuccess) onSuccess();
      else setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not switch to LIVE. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const blockers = readiness?.blocking_failures || [];
  const warnings = readiness?.warnings || [];
  const ready = !!readiness?.ready && blockers.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="go-live-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-emerald-500" /> Switch Brain to LIVE
          </DialogTitle>
          <DialogDescription>
            In LIVE mode the AI Brain sends <strong>real</strong> emails, calls and messages to your
            leads on your active cadences. This takes effect immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1" data-testid="go-live-readiness">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="go-live-readiness-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking readiness…
            </div>
          ) : error ? (
            <div className="space-y-2" data-testid="go-live-readiness-error">
              <div className="flex items-start gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                Couldn't verify readiness. Please check your connection and try again.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadReadiness(true)}
                data-testid="go-live-readiness-retry"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Retry
              </Button>
            </div>
          ) : (
            <>
              {ready && (
                <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="go-live-ready">
                  <CheckCircle2 className="h-4 w-4" /> Your workspace is ready for LIVE mode.
                </div>
              )}
              {!ready && (
                <div className="flex items-center gap-2 text-sm text-amber-600" data-testid="go-live-not-ready">
                  <AlertTriangle className="h-4 w-4" /> A few things need attention before going LIVE.
                </div>
              )}
              {blockers.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-red-600" data-testid="go-live-blocker">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {b}
                </div>
              ))}
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-600/90">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}
                </div>
              ))}
            </>
          )}
        </div>

        {!loading && !error && (
          <label
            className="flex items-start gap-3 rounded-lg border bg-muted/40 p-3 cursor-pointer"
            data-testid="go-live-autopilot-toggle"
          >
            <Checkbox
              checked={autopilot}
              onCheckedChange={(v) => setAutopilot(!!v)}
              className="mt-0.5"
              data-testid="go-live-autopilot-checkbox"
            />
            <span className="text-sm">
              <span className="flex items-center gap-1.5 font-medium">
                <Zap className="h-3.5 w-3.5 text-amber-500" /> Enable full autopilot
              </span>
              <span className="text-xs text-muted-foreground">
                AI sends emails and advances cadences without waiting for your approval on each lead.
                Uncheck to keep semi-auto (human approval on low-scored leads).
              </span>
            </span>
          </label>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="go-live-cancel">Cancel</Button>
          <Button
            onClick={goLive}
            disabled={!ready || submitting || loading || error}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="go-live-confirm"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Go LIVE now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoLiveDialog;
